import { Appointment } from '../../../types';
import { ContextDataItem } from '../../../types/widget';

const DEFAULT_FORMATTERS: Record<string, (value: unknown) => string> = {
  serviceDate: (date: unknown) => {
    if (!date) return '';

    return new Date(date as string).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  },
};

export const processLabel = (
  label: string,
  appointment?: Appointment
): string => {
  if (!appointment) return label;

  return label.replace(/\{\{appointment\.(\w+)\}\}/g, (match, field) => {
    const value = appointment[field];

    if (value == null) return '';

    if (DEFAULT_FORMATTERS[field]) {
      return DEFAULT_FORMATTERS[field](value);
    }

    return match;
  });
};

const normalizeValue = (value: unknown): unknown => {
  if (typeof value === 'string' && value.trim() === '') {
    return undefined;
  }
  return value;
};

const shouldIncludeItem = (item: ContextDataItem): boolean => {
  return item.hidden ? Boolean(item.value) : Boolean(item.selected);
};

const normalizeContextData = (data: ContextDataItem[]): string => {
  const normalized = data
    .filter(shouldIncludeItem)
    .map((item) => {
      const normalizedValue = normalizeValue(item.value);
      const normalizedChildren = item.children
        ?.filter(shouldIncludeItem)
        .map((child) => ({
          key: child.key,
          value: normalizeValue(child.value),
        }));

      return {
        key: item.key,
        value: normalizedValue,
        ...(normalizedChildren?.length ? { children: normalizedChildren } : {}),
      };
    })
    .filter((item) => item.value !== undefined)
    .sort((x, y) => String(x.key).localeCompare(String(y.key)));

  return JSON.stringify(normalized);
};

export const isContextDataDirty = (
  current: ContextDataItem[],
  lastGeneratedContext: ContextDataItem[] | null
): boolean => {
  if (!lastGeneratedContext) return false;
  return (
    normalizeContextData(current) !== normalizeContextData(lastGeneratedContext)
  );
};
